import * as React from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { User, UserRole } from '../types';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format } from 'date-fns';
import { 
  Download, 
  Edit, 
  Info, 
  Key, 
  Lock, 
  Mail, 
  Shield, 
  Trash2, 
  UserCog
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import { UsersService } from '@/services/users-service';
import { useToast } from '@/hooks/use-toast';

const roleColors: Record<UserRole, string> = {
  admin: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  editor: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  contributor: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  subscriber: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
};

const roleLabels: Record<UserRole, string> = {
  admin: 'Administrator',
  editor: 'Editor',
  contributor: 'Contributor',
  subscriber: 'Subscriber',
};

export function UserDetailsPage() {
  const { id } = useParams({ from: '/_authenticated/users/$id' });
  const [user, setUser] = React.useState<User | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const { toast } = useToast();

  React.useEffect(() => {
    const fetchUser = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const fetchedUser = await UsersService.getUser(id);
        setUser(fetchedUser);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch user';
        setError(errorMessage);
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  const handlePasswordReset = async () => {
    try {
      await UsersService.resetUserPassword(id);
      toast({
        title: 'Success',
        description: 'Password reset request sent successfully',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset password';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await UsersService.deleteUser(id);
        toast({
          title: 'Success',
          description: 'User deleted successfully',
        });
        // Navigate back to users list
        window.location.href = '/users';
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to delete user';
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        });
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading user...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <Alert variant="destructive">
        <Info className="h-4 w-4" />
        <AlertTitle>User Not Found</AlertTitle>
        <AlertDescription>
          The user with ID {id} could not be found. They may have been deleted or you may not have permission to view them.
        </AlertDescription>
      </Alert>
    );
  }

  const createdDate = new Date(user.createdAt);
  const lastLoginDate = user.lastLogin ? new Date(user.lastLogin) : null;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <PageHeader
          title={user.fullName}
          description={`@${user.username} • ${roleLabels[user.role]}`}
        />
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
          <Button variant="outline" size="sm" onClick={handlePasswordReset}>
            <Lock className="mr-2 h-4 w-4" />
            Reset Password
          </Button>
          <Button asChild size="sm">
            <Link to="/users/edit/$id" params={{ id: user.id }}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Link>
          </Button>
          <Button variant="destructive" size="sm" onClick={handleDelete}>
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Badge 
          variant="outline" 
          className={roleColors[user.role]}
        >
          {roleLabels[user.role]}
        </Badge>
        <Badge 
          variant="outline" 
          className={
            user.isActive
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
              : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
          }
        >
          {user.isActive ? 'Active' : 'Inactive'}
        </Badge>
        <Badge 
          variant={user.twoFactorEnabled ? 'default' : 'outline'}
        >
          {user.twoFactorEnabled ? '2FA Enabled' : '2FA Disabled'}
        </Badge>
      </div>

      <Tabs defaultValue="details" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="details">User Details</TabsTrigger>
          <TabsTrigger value="permissions">Permissions</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-4 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Profile</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col items-center">
                  <Avatar className="h-24 w-24 mb-4">
                    <AvatarImage src={user.avatar} alt={user.fullName} />
                    <AvatarFallback className="text-2xl">{user.fullName.substring(0, 2).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <h3 className="text-lg font-medium">{user.fullName}</h3>
                  <p className="text-sm text-muted-foreground">@{user.username}</p>
                  <div className="mt-4 w-full">
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm font-medium">Email</span>
                      <span className="text-sm text-muted-foreground flex items-center">
                        <Mail className="h-3 w-3 mr-1" />
                        {user.email}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm font-medium">Role</span>
                      <Badge className={roleColors[user.role]} variant="outline">
                        {roleLabels[user.role]}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm font-medium">Status</span>
                      <Badge 
                        variant="outline" 
                        className={
                          user.isActive
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                        }
                      >
                        {user.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm font-medium">2FA</span>
                      <Badge variant={user.twoFactorEnabled ? 'default' : 'outline'}>
                        {user.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <div className="md:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Account Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Username</span>
                    <span className="text-sm text-muted-foreground">{user.username}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Email</span>
                    <span className="text-sm text-muted-foreground">{user.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Full Name</span>
                    <span className="text-sm text-muted-foreground">{user.fullName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Created</span>
                    <span className="text-sm text-muted-foreground">{format(createdDate, 'PPpp')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Last Login</span>
                    <span className="text-sm text-muted-foreground">
                      {lastLoginDate ? format(lastLoginDate, 'PPpp') : 'Never'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Active Status</span>
                    <div className="flex items-center">
                      <Switch checked={user.isActive} disabled />
                      <span className="ml-2 text-sm text-muted-foreground">{user.isActive ? 'Active' : 'Inactive'}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Two-Factor Authentication</span>
                    <div className="flex items-center">
                      <Switch checked={user.twoFactorEnabled} disabled />
                      <span className="ml-2 text-sm text-muted-foreground">{user.twoFactorEnabled ? 'Enabled' : 'Disabled'}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="permissions" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Role Permissions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-medium mb-2">{roleLabels[user.role]}</h3>
                  <p className="text-sm text-muted-foreground">
                    This user has {roleLabels[user.role].toLowerCase()} permissions in the system.
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div className="text-center py-8">
                    <div className="mx-auto h-12 w-12 bg-muted rounded-full flex items-center justify-center mb-4">
                      <Shield className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Detailed role permissions will be available in a future update.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activity" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Account Activity</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="mr-4 mt-0.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary">
                      <UserCog className="h-4 w-4 text-primary-foreground" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Account Created</p>
                    <p className="text-xs text-muted-foreground">
                      {format(createdDate, 'PPpp')}
                    </p>
                  </div>
                </div>

                {lastLoginDate && (
                  <>
                    <Separator />
                    <div className="flex items-start">
                      <div className="mr-4 mt-0.5">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                          <Key className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-medium">Last Login</p>
                        <p className="text-xs text-muted-foreground">
                          {format(lastLoginDate, 'PPpp')}
                        </p>
                      </div>
                    </div>
                  </>
                )}

                {user.twoFactorEnabled && (
                  <>
                    <Separator />
                    <div className="flex items-start">
                      <div className="mr-4 mt-0.5">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
                          <Shield className="h-4 w-4 text-blue-600 dark:text-blue-300" />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-medium">Two-Factor Authentication Enabled</p>
                        <p className="text-xs text-muted-foreground">
                          Account has additional security
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
